import { EMPTY, formatDate, formatTime, formatValue, isNumber, toInstant } from './piFormat.js';

export const STATE_INFO = {
  ok: {
    label: 'Medido',
    description: 'Valor calculado com dados completos do período.',
    tone: 'ok',
  },
  partial: {
    label: 'Parcial',
    description: 'O período ainda não fechou: o valor pode mudar até o fechamento diário.',
    tone: 'warn',
  },
  na: {
    label: 'n/d',
    description: 'Indicador indisponível para este recorte.',
    tone: 'muted',
  },
  insufficient: {
    label: 'Dados insuficientes',
    description: 'A amostra é pequena demais para um número confiável.',
    tone: 'muted',
  },
  immature: {
    label: 'Pendente',
    description: 'A coorte ainda não completou a janela; o valor sai na data de maturação.',
    tone: 'info',
  },
  derived: {
    label: 'Retroativo',
    description: 'Calculado a partir do histórico já gravado (derivado), com semântica mais simples que a coleta instrumentada.',
    tone: 'info',
  },
  disabled: {
    label: 'Não habilitada',
    description: 'Nenhuma empresa do recorte tem esta funcionalidade ligada.',
    tone: 'muted',
  },
  no_eligible: {
    label: 'Sem elegíveis',
    description: 'A funcionalidade está ligada, mas nenhum perfil aplicável está habilitado.',
    tone: 'muted',
  },
  no_usage: {
    label: 'Sem uso',
    description: 'Há elegíveis, mas nenhum uso no período.',
    tone: 'warn',
  },
  not_instrumented: {
    label: 'Sem medição',
    description: 'Nenhum evento desta funcionalidade é medido ainda.',
    tone: 'muted',
  },
};

export const STATES = Object.keys(STATE_INFO);

const HIDDEN_VALUE_STATES = ['na', 'insufficient', 'immature', 'disabled', 'no_eligible', 'not_instrumented'];

const UNAVAILABLE_STATES = ['na', 'not_instrumented'];

const UNAVAILABLE_NOTE = /indispon[ií]ve/i;

const UNITS = ['count', 'pct', 'ratio', 'days'];

const known = (state) => Object.prototype.hasOwnProperty.call(STATE_INFO, state);

const numberOrNull = (value) => (isNumber(value) ? value : null);

export const stateLabel = (state) => (known(state) ? STATE_INFO[state].label : STATE_INFO.na.label);

export const stateTone = (state) => (known(state) ? STATE_INFO[state].tone : STATE_INFO.na.tone);

export const stateDescription = (state, reason) => {
  const base = known(state) ? STATE_INFO[state].description : STATE_INFO.na.description;
  return reason ? base + ' Motivo: ' + reason : base;
};

export const computeDelta = (value, previous) => {
  if (!isNumber(value) || !isNumber(previous)) return { deltaAbs: null, deltaPct: null };
  const deltaAbs = value - previous;
  const deltaPct = previous > 0 ? (deltaAbs / previous) * 100 : null;
  return { deltaAbs: deltaAbs, deltaPct: deltaPct };
};

export const normalizeMetric = (raw) => {
  const source = raw && typeof raw === 'object' ? raw : {};
  const value = numberOrNull(source.value);
  const previous = numberOrNull(source.previous);
  const unit = UNITS.indexOf(source.unit) !== -1 ? source.unit : 'count';
  let state = known(source.state) ? source.state : null;
  if (!state) state = value === null ? 'na' : 'ok';
  const computed = computeDelta(value, previous);
  return {
    value: value,
    previous: previous,
    deltaAbs: isNumber(source.deltaAbs) ? source.deltaAbs : computed.deltaAbs,
    deltaPct: isNumber(source.deltaPct) ? source.deltaPct : computed.deltaPct,
    numerator: numberOrNull(source.numerator),
    denominator: numberOrNull(source.denominator),
    unit: unit,
    state: state,
    stateReason: typeof source.stateReason === 'string' && source.stateReason ? source.stateReason : null,
    definitionKey: typeof source.definitionKey === 'string' && source.definitionKey ? source.definitionKey : null,
  };
};

export const showsValue = (metric) =>
  !!metric && isNumber(metric.value) && HIDDEN_VALUE_STATES.indexOf(metric.state) === -1;

export const needsBadge = (metric) => !!metric && metric.state !== 'ok';

export const deltaPctAllowed = (metric) =>
  !!metric && isNumber(metric.previous) && metric.previous > 0 && isNumber(metric.deltaPct);

export const deltaAllowed = (metric) =>
  showsValue(metric) && isNumber(metric.previous) && isNumber(metric.deltaAbs);

export const metricText = (metric) => {
  if (!metric) return EMPTY;
  if (showsValue(metric)) return formatValue(metric.value, metric.unit);
  return stateLabel(metric.state);
};

export const immatureText = (maturityDate) => {
  const date = formatDate(maturityDate);
  return date === EMPTY ? 'pendente' : 'pendente ' + date;
};

export const envelopeStatus = (envelope) => {
  const source = envelope && typeof envelope === 'object' ? envelope : {};
  const coverage = source.coverage && typeof source.coverage === 'object' ? source.coverage : {};
  const asOf = source.asOf || null;
  const instant = toInstant(asOf);
  const partial = source.isPartial === true;
  return {
    asOf: asOf,
    partial: partial,
    partialText: partial ? (instant ? 'Dados parciais: até ' + formatTime(instant) + '.' : 'Dados parciais.') : '',
    asOfText: instant ? 'Atualizado em ' + formatDate(instant) + ' às ' + formatTime(instant) : '',
    definitionVersion: isNumber(source.definitionVersion) ? source.definitionVersion : null,
    collectionStartedAt: coverage.collectionStartedAt || null,
    backfilledFrom: coverage.backfilledFrom || null,
    notes: Array.isArray(source.notes) ? source.notes.filter((note) => typeof note === 'string' && note) : [],
  };
};

export const emptyText = (envelope) => {
  const status = envelopeStatus(envelope);
  const started = formatDate(status.collectionStartedAt);
  const base = 'Sem eventos no período.';
  return started === EMPTY ? base : base + ' A coleta começou em ' + started + '.';
};

const lastOf = (list) => (list.length ? list[list.length - 1] : '');

export const unavailableNotes = (envelope) => envelopeStatus(envelope).notes.filter((note) => UNAVAILABLE_NOTE.test(note));

export const unavailableReason = (envelope, empty) => {
  if (!envelope || typeof envelope !== 'object') return '';
  const flagged = lastOf(unavailableNotes(envelope));
  const explicit = typeof envelope.stateReason === 'string' && envelope.stateReason ? envelope.stateReason : '';
  const fallback = explicit || flagged || lastOf(envelopeStatus(envelope).notes);
  if (UNAVAILABLE_STATES.indexOf(envelope.state) !== -1) return fallback || stateDescription(envelope.state);
  if (envelope.data === null) return fallback;
  return empty ? flagged : '';
};

export const notesWithout = (envelope, shown) => {
  const skip = Array.isArray(shown) ? shown : [shown];
  return envelopeStatus(envelope).notes.filter((note) => skip.indexOf(note) === -1);
};

const SOURCE_API_LABELS = { 1: 'API V1', 2: 'API V2', 3: 'Derivação' };

const SOURCE_KIND_LABELS = { I: 'Instrumentado', D: 'Derivado', B: 'Retroativo' };

const BREAKER_INFO = {
  closed: { label: 'Fechado', tone: 'ok' },
  open: { label: 'Aberto: descartando', tone: 'bad' },
  half_open: { label: 'Meio-aberto', tone: 'warn' },
  halfopen: { label: 'Meio-aberto', tone: 'warn' },
};

const JOB_STATUS_INFO = {
  ok: { label: 'OK', tone: 'ok' },
  success: { label: 'OK', tone: 'ok' },
  succeeded: { label: 'OK', tone: 'ok' },
  running: { label: 'Rodando', tone: 'info' },
  partial: { label: 'Parcial', tone: 'warn' },
  failed: { label: 'Falhou', tone: 'bad' },
  error: { label: 'Falhou', tone: 'bad' },
  blocked_index: { label: 'Bloqueado: índice', tone: 'warn' },
  disabled: { label: 'Desligado', tone: 'muted' },
  skipped: { label: 'Pulado', tone: 'muted' },
};

const lowerKey = (value) => (typeof value === 'string' ? value.trim().toLowerCase().replace(/-/g, '_') : '');

export const sourceApiLabel = (value) => SOURCE_API_LABELS[Number(value)] || EMPTY;

export const sourceKindLabel = (value) => SOURCE_KIND_LABELS[value] || (value ? String(value) : EMPTY);

export const breakerInfo = (value) => {
  const key = lowerKey(value);
  if (!key) return { label: EMPTY, tone: 'muted' };
  return BREAKER_INFO[key] || { label: String(value), tone: 'muted' };
};

export const jobStatusInfo = (value) => {
  const key = lowerKey(value);
  if (!key) return { label: EMPTY, tone: 'muted' };
  return JOB_STATUS_INFO[key] || { label: String(value), tone: 'muted' };
};

export const HEARTBEAT_STALE_MINUTES = 5;

export const heartbeatStale = (minutesSinceHeartbeat) =>
  !isNumber(minutesSinceHeartbeat) || minutesSinceHeartbeat > HEARTBEAT_STALE_MINUTES;



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piState.js